import React, { useRef, useState, useEffect } from 'react';
import { Box, Tooltip, Typography, Paper } from '@mui/material';
import CameraAlt from '@mui/icons-material/CameraAlt';
import Close from '@mui/icons-material/Close';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import FlipCameraIos from '@mui/icons-material/FlipCameraIos';
import CloudUpload from '@mui/icons-material/CloudUpload';

import { useNotification } from '../../hooks/useNotification';

const PhotoCapture = ({
    field,
    value,
    onChange,
    error,
    helperText,
    required
}) => {
    const [sendNotification] = useNotification();
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const streamRef = useRef(null);
    const fileInputRef = useRef(null);

    const timestamp = new Date().getTime();
    const [isActive, setIsActive] = useState(false);
    const [imageBase64, setImageBase64] = useState(value || '');
    const [facingMode, setFacingMode] = useState('environment');
    const [fileName, setFileName] = useState('');

    useEffect(() => {
        return () => {
            if (streamRef.current)
                streamRef.current.getTracks().forEach(track => track.stop());
        };
    }, []);

    const startCamera = async () => {
        try {
            stopCameraTracks();
            const mediaStream = await navigator.mediaDevices.getUserMedia({
                video: {
                    facingMode: facingMode,
                    width: { ideal: 1280 },
                    height: { ideal: 720 }
                }
            });

            streamRef.current = mediaStream;
            setIsActive(true);

            setTimeout(() => {
                if (videoRef.current)
                    videoRef.current.srcObject = mediaStream;
            }, 150);
        } catch (err) {
            sendNotification({ msg: "No se pudo acceder a la cámara. Verifica los permisos de tu navegador.", variant: 'info' });
        }
    };

    const stopCameraTracks = () => {
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
            streamRef.current = null;
        }
    };

    const handleCloseCamera = () => {
        stopCameraTracks();
        setIsActive(false);
    };

    const setData = (name, base64) => {
        setImageBase64(base64);
        setFileName(name);
        onChange(field.id + '_file_B64', base64.split(",")[1]);
        onChange(field.id, name);
    }

    const capturePhoto = () => {
        if (videoRef.current && canvasRef.current) {
            const video = videoRef.current;
            const canvas = canvasRef.current;

            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;

            const context = canvas.getContext('2d');
            context.drawImage(video, 0, 0);
            const base64 = canvas.toDataURL('image/jpeg', 0.8);
            setData(timestamp + '.jpg', base64);
            handleCloseCamera();
        }
    };

    const handleFileUpload = (event) => {
        const file = event.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (e) => {
                const base64 = e.target.result;
                setData(file.name, base64);
            };
            reader.readAsDataURL(file);
        }
        event.target.value = '';
    };

    const removePhoto = () => setData('', '');
    const toggleCamera = () => setFacingMode(prev => (prev === 'environment' ? 'user' : 'environment'));

    useEffect(() => {
        if (isActive)
            startCamera();
    }, [facingMode]);

    return (
        <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color={error ? 'error' : 'textSecondary'} sx={{ fontWeight: 'medium' }}>
                {field.label} {required && '*'}
            </Typography>

            <Paper
                elevation={1}
                sx={{
                    mt: 1,
                    p: 1,
                    minHeight: 220,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: '#fafafa',
                    border: error ? '1px solid #d32f2f' : '1px solid #e0e0e0',
                    position: 'relative',
                    overflow: 'hidden'
                }}
            >
                {imageBase64 ? (
                    <Box sx={{ width: '100%', textAlign: 'center' }}>
                        <Box
                            component="img"
                            src={imageBase64}
                            alt="Preview"
                            sx={{
                                width: '100%',
                                maxHeight: 300,
                                objectFit: 'contain',
                                borderRadius: 1
                            }}
                        />
                        {fileName && (
                            <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, display: 'block' }}>
                                {fileName}
                            </Typography>
                        )}
                        <Box sx={{ mt: 1 }}>
                            <Tooltip title="Eliminar y tomar otra">
                                <Close
                                    sx={{ fontSize: 35, cursor: 'pointer' }}
                                    color="error"
                                    onClick={removePhoto}
                                />
                            </Tooltip>
                        </Box>
                    </Box>
                ) : isActive ? (
                    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <video
                            ref={videoRef}
                            autoPlay
                            playsInline
                            muted
                            style={{
                                width: '100%',
                                maxHeight: 250,
                                objectFit: 'cover',
                                borderRadius: 8,
                                backgroundColor: '#000'
                            }}
                        />
                        <Box sx={{ display: 'flex', gap: 2, mt: 2, pb: 1 }}>
                            <Tooltip title="Cambiar cámara">
                                <FlipCameraIos
                                    sx={{ fontSize: 35, cursor: 'pointer' }}
                                    color='primary'
                                    onClick={toggleCamera}
                                />
                            </Tooltip>
                            <Tooltip title="Capturar Foto">
                                <PhotoCamera
                                    sx={{ fontSize: 35, cursor: 'pointer' }}
                                    color="success"
                                    onClick={capturePhoto}
                                />
                            </Tooltip>
                            <Tooltip title="Cancelar">
                                <Close
                                    sx={{ fontSize: 35, cursor: 'pointer' }}
                                    color="error"
                                    onClick={handleCloseCamera}
                                />
                            </Tooltip>
                        </Box>
                    </Box>
                ) : (
                    <Box sx={{ textAlign: 'center', py: 2 }}>
                        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                            Capturar foto o cargar imagen
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                            <Tooltip title="Abrir cámara">
                                <CameraAlt
                                    sx={{ fontSize: 35, cursor: 'pointer' }}
                                    color='primary'
                                    onClick={startCamera}
                                />
                            </Tooltip>
                            <Tooltip title="Cargar imagen (JPG, PNG, PDF)">
                                <CloudUpload
                                    sx={{ fontSize: 35, cursor: 'pointer' }}
                                    color="success"
                                    onClick={() => fileInputRef.current?.click()}
                                />
                            </Tooltip>
                        </Box>
                    </Box>
                )}
            </Paper>

            {error && helperText && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block', ml: 1 }}>
                    {helperText}
                </Typography>
            )}

            <canvas ref={canvasRef} style={{ display: 'none' }} />
            <input
                ref={fileInputRef}
                type="file"
                accept=".jpg,.jpeg,.png,.pdf"
                style={{ display: 'none' }}
                onChange={handleFileUpload}
            />
        </Box>
    );
};

export default PhotoCapture;
